<template>
  <div class="ws-history">
    <div v-if="loading && !history.length" class="ws-history__state">
      <v-progress-circular indeterminate size="28" width="3" color="primary" />
    </div>
    <div v-else-if="!history.length" class="ws-history__state">No history yet</div>
    <v-timeline v-else dense align-top class="ws-history__timeline">
      <v-timeline-item v-for="event in history" :key="event.id" x-small :color="eventColor(event.eventType)">
        <div class="ws-history__text">{{ describe(event) }}</div>
        <div class="ws-history__meta">
          {{ $formatDate(event.occurredAt) }}<span v-if="event.source"> · {{ event.source }}</span>
        </div>
      </v-timeline-item>
    </v-timeline>
    <div v-if="history.length < total" class="ws-history__more">
      <v-btn small text color="primary" :loading="loading" @click="load(page + 1)">Load more</v-btn>
    </div>
  </div>
</template>

<script>
  import documentVersionsService from '../../service/documentVersions.service';
  import { $formatDate, $handleError } from '@/utils';
  import { describeHistoryEvent } from '../../utils/documentVersions';

  const PAGE_SIZE = 50;

  /** Event log of one document. Loads the first time it becomes `active`. */
  export default {
    name: 'WorkspaceHistoryTab',
    props: {
      documentId: { type: [String, Number], required: true },
      versionNumbers: { type: Object, default: () => ({}) },
      active: { type: Boolean, default: false },
    },
    data() {
      return {
        history: [],
        total: 0,
        page: 0,
        loading: false,
        requestSeq: 0,
      };
    },
    watch: {
      documentId() {
        this.reset();
      },
      active: {
        immediate: true,
        handler(active) {
          if (active && this.page === 0) this.load(1);
        },
      },
    },
    methods: {
      $formatDate,
      /** Forget what was loaded; reloads now when visible, else the next time it is shown. */
      reset() {
        // Invalidate any request still in flight for the previous document.
        this.requestSeq++;
        this.loading = false;
        this.history = [];
        this.total = 0;
        this.page = 0;
        if (this.active) this.load(1);
      },
      load(page) {
        // "Load more" while a page is in flight would append the same page twice.
        if (page > 1 && this.loading) return Promise.resolve();
        const seq = ++this.requestSeq;
        this.loading = true;
        return documentVersionsService
          .getHistory(this.documentId, { page, itemsPerPage: PAGE_SIZE })
          .then((res) => {
            if (seq !== this.requestSeq) return;
            const items = res.data?.items || [];
            this.history = page === 1 ? items : this.history.concat(items);
            this.total = res.data?.total || 0;
            this.page = page;
          })
          .catch((err) => {
            if (seq === this.requestSeq) $handleError(err);
          })
          .finally(() => {
            if (seq === this.requestSeq) this.loading = false;
          });
      },
      describe(event) {
        return describeHistoryEvent(event, this.versionNumbers);
      },
      eventColor(type) {
        switch (type) {
          case 'CurrentSet':
          case 'CurrentCleared':
            return 'warning';
          case 'Deleted':
          case 'Purged':
            return 'error';
          case 'VersionAdded':
          case 'Created':
            return 'primary';
          default:
            return 'grey';
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ws-history {
    padding: 4px 8px 8px 0;
  }

  .ws-history__state {
    display: flex;
    justify-content: center;
    padding: 32px 12px;
    opacity: 0.75;
  }

  .ws-history__text {
    font-size: var(--ds-font-body-sm, 13px);
  }

  .ws-history__meta {
    font-size: var(--ds-font-caption, 12px);
    opacity: 0.7;
  }

  .ws-history__more {
    display: flex;
    justify-content: center;
  }
</style>
